import * as THREE from 'three'
import { ImportedModelObjectMetadataService } from '../../metadata'
import {
  ImportedModelTopologyRepository,
  registerImportedModelTopologyRepository,
  resolveImportedModelTopologyRepository,
} from '../../topology/ImportedModelTopologyRepository'
import type { ImportedMeshTopologyBinding } from '../../topology/ImportedMeshTopologyBinding'
import type { ImportedModelInstancedMeshUnit } from '../mesh-unit'
import type { ImportedModelSourceMeshVisibilityController } from './ImportedModelSourceMeshVisibilityController'
import { moveObject3DChildToIndex } from '../../../utils/scene-object/addObject3DToParent'

/** 派生投影根到 source Mesh 控制器的非持久化运行时键。 */
const SOURCE_MESH_EXPORT_CONTROLLER = Symbol('PuppyCAD.ImportedModelSourceMeshExportController')

/** 派生投影根到正式实例 Unit 的非持久化运行时键。 */
const SOURCE_MESH_EXPORT_UNITS = Symbol('PuppyCAD.ImportedModelSourceMeshExportUnits')

/** 派生投影根到 source Mesh 原始 Body 拓扑记录的非持久化运行时键。 */
const SOURCE_MESH_EXPORT_BODY_RECORDS = Symbol('PuppyCAD.ImportedModelSourceMeshExportBodyRecords')

/**
 * 协调实例派生渲染与同步离线快照的场景读取边界。
 *
 * 正式渲染期间，业务 source Mesh 已从主场景树脱离，派生 InstancedMesh 位于主场景中。
 * 离线导出使用 occurrence 级单槽实例快照，不恢复 source Mesh；只有显示模式等必须读取业务
 * Mesh 的同步场景操作才临时恢复，并在 finally 中重新脱离。
 */
export class ImportedModelSourceMeshExportBridge {
  /**
   * 注册一个已经发布到主场景的实例派生根。
   *
   * @param projectionRoot 当前 generation 的派生投影根。
   * @param controller 持有同代 source Mesh 脱离状态的控制器。
   * @param units 当前 generation 已发布的正式实例 Unit。
   */
  public register(
    projectionRoot: THREE.Object3D,
    controller: ImportedModelSourceMeshVisibilityController,
    units: readonly ImportedModelInstancedMeshUnit[],
  ): void {
    Reflect.set(projectionRoot, SOURCE_MESH_EXPORT_CONTROLLER, controller)
    Reflect.set(projectionRoot, SOURCE_MESH_EXPORT_UNITS, Object.freeze([...units]))
    const bodyRecordsBySourceMesh = new Map<THREE.Mesh, readonly ImportedMeshTopologyBinding[]>()
    units.forEach((unit) => {
      unit.listSourceMeshes().forEach((sourceMesh) => {
        const bodyRecords = resolveImportedModelTopologyRepository(sourceMesh)
          ?.resolveBodyRecordsByVisibleMesh(sourceMesh) ?? []
        bodyRecordsBySourceMesh.set(sourceMesh, Object.freeze(bodyRecords))
      })
    })
    Reflect.set(projectionRoot, SOURCE_MESH_EXPORT_BODY_RECORDS, bodyRecordsBySourceMesh)
  }

  /**
   * 注销已经销毁或即将重建的实例派生根。
   *
   * @param projectionRoot 待注销派生根。
   */
  public unregister(projectionRoot: THREE.Object3D): void {
    Reflect.deleteProperty(projectionRoot, SOURCE_MESH_EXPORT_CONTROLLER)
    Reflect.deleteProperty(projectionRoot, SOURCE_MESH_EXPORT_UNITS)
    Reflect.deleteProperty(projectionRoot, SOURCE_MESH_EXPORT_BODY_RECORDS)
  }

  /**
   * 使用单槽 InstancedMesh 快照替代脱离的业务 source Mesh 执行同步导出。
   *
   * 该入口不会把 source Mesh 挂回主场景。每个 occurrence 生成一个单槽实例快照，使既有
   * assembly/body/topology 绑定继续保持一对一，同时由 GLTFExporter 写入
   * `EXT_mesh_gpu_instancing`。正式多槽 Unit 仍属于运行时派生根，不直接进入业务文档。
   *
   * @param sceneRoot 当前离线快照遍历根。
   * @param reader 同步可见模型快照读取函数。
   * @returns 读取函数返回值。
   */
  public readInstancedExportScene<T>(sceneRoot: THREE.Object3D, reader: () => T): T {
    const exportProxies: THREE.InstancedMesh[] = []
    const exportRepositories: ImportedModelTopologyRepository[] = []
    const projectionRoots = this.collectProjectionRoots(sceneRoot)

    projectionRoots.forEach((projectionRoot) => {
      const controller = Reflect.get(
        projectionRoot,
        SOURCE_MESH_EXPORT_CONTROLLER,
      ) as ImportedModelSourceMeshVisibilityController | undefined
      const units = Reflect.get(
        projectionRoot,
        SOURCE_MESH_EXPORT_UNITS,
      ) as readonly ImportedModelInstancedMeshUnit[] | undefined
      const bodyRecordsBySourceMesh = Reflect.get(
        projectionRoot,
        SOURCE_MESH_EXPORT_BODY_RECORDS,
      ) as ReadonlyMap<THREE.Mesh, readonly ImportedMeshTopologyBinding[]> | undefined
      if (!controller || !units) {
        return
      }
      units.forEach((unit) => {
        unit.listSourceMeshes().forEach((sourceMesh) => {
          const proxy = this.createOccurrenceInstancedExportProxy(
            sourceMesh,
            unit.resolveOccurrenceRoot(sourceMesh),
            controller,
          )
          if (proxy) {
            exportProxies.push(proxy)
            const bodyRecords = bodyRecordsBySourceMesh?.get(sourceMesh) ?? []
            if (bodyRecords.length > 0) {
              exportRepositories.push(this.createExportTopologyRepository(proxy, bodyRecords))
            }
          }
        })
      })
    })

    try {
      return reader()
    } finally {
      for (let index = exportRepositories.length - 1; index >= 0; index -= 1) {
        exportRepositories[index]?.dispose()
      }
      for (let index = exportProxies.length - 1; index >= 0; index -= 1) {
        const proxy = exportProxies[index]
        if (!proxy) {
          continue
        }
        proxy.removeFromParent()
        proxy.dispose()
      }
    }
  }

  /**
   * 为实例导出代理建立只在本次快照中有效的 Body 拓扑仓库。
   *
   * @param proxy occurrence 级单槽实例代理。
   * @param sourceRecords 原 source Mesh 的 Body 拓扑记录。
   * @returns 已注册到代理自身的临时仓库。
   */
  private createExportTopologyRepository(
    proxy: THREE.InstancedMesh,
    sourceRecords: readonly ImportedMeshTopologyBinding[],
  ): ImportedModelTopologyRepository {
    const repository = new ImportedModelTopologyRepository(proxy)
    repository.registerVisibleMesh(
      proxy,
      sourceRecords.map(record => record.bodyBinding),
      Object.fromEntries(sourceRecords.map(record => [
        record.bodyBindingId,
        record.topologyAsset,
      ])),
    )
    registerImportedModelTopologyRepository(proxy, repository)
    return repository
  }

  /**
   * 在业务源 Mesh 临时挂回场景、派生实例根临时移除的条件下执行同步读取。
   *
   * @param sceneRoot 当前导出遍历根。
   * @param reader 不得跨微任务保存场景引用的同步读取函数。
   * @returns 读取函数返回值。
   */
  public readBusinessScene<T>(sceneRoot: THREE.Object3D, reader: () => T): T {
    const projectionRecords: Array<readonly [
      THREE.Object3D,
      THREE.Object3D,
      number,
      ImportedModelSourceMeshVisibilityController,
      readonly THREE.Mesh[],
    ]> = []

    const projectionRoots = this.collectProjectionRoots(sceneRoot)
    projectionRoots.forEach((projectionRoot) => {
      const controller = Reflect.get(
        projectionRoot,
        SOURCE_MESH_EXPORT_CONTROLLER,
      ) as ImportedModelSourceMeshVisibilityController | undefined
      const projectionParent = projectionRoot.parent
      if (!controller || !projectionParent || !this.isDescendantOrSelf(projectionRoot, sceneRoot)) {
        return
      }
      const projectionIndex = projectionParent.children.indexOf(projectionRoot)
      projectionRoot.removeFromParent()
      const restoredSourceMeshes = controller.restoreSourceMeshesForRead(sceneRoot)
      projectionRecords.push([
        projectionRoot,
        projectionParent,
        projectionIndex,
        controller,
        restoredSourceMeshes,
      ])
    })

    try {
      return reader()
    } finally {
      for (let index = projectionRecords.length - 1; index >= 0; index -= 1) {
        const record = projectionRecords[index]
        if (!record) {
          continue
        }
        const [projectionRoot, projectionParent, projectionIndex, controller, sourceMeshes] = record
        controller.detachSourceMeshesAfterRead(sourceMeshes)
        projectionParent.add(projectionRoot)
        const insertionIndex = Math.min(
          Math.max(0, projectionIndex),
          Math.max(0, projectionParent.children.length - 1),
        )
        moveObject3DChildToIndex(projectionParent, projectionRoot, insertionIndex)
      }
    }
  }

  /**
   * 创建一个 occurrence 对应的单槽实例导出代理并挂到原业务位置。
   *
   * @param sourceMesh 保持脱离状态的业务 source Mesh。
   * @param occurrenceRoot 当前 source 对应的组件变换根。
   * @param controller source Mesh 脱离与有效世界矩阵控制器。
   * @returns 已挂载代理；缺少有效父级或变换根时返回 null。
   */
  private createOccurrenceInstancedExportProxy(
    sourceMesh: THREE.Mesh,
    occurrenceRoot: THREE.Object3D | null,
    controller: ImportedModelSourceMeshVisibilityController,
  ): THREE.InstancedMesh | null {
    if (!occurrenceRoot) {
      return null
    }

    const proxy = new THREE.InstancedMesh(sourceMesh.geometry, sourceMesh.material, 1)
    proxy.name = sourceMesh.name
    proxy.visible = sourceMesh.visible
    proxy.castShadow = sourceMesh.castShadow
    proxy.receiveShadow = sourceMesh.receiveShadow
    proxy.renderOrder = sourceMesh.renderOrder
    proxy.layers.mask = sourceMesh.layers.mask
    proxy.frustumCulled = sourceMesh.frustumCulled
    proxy.userData = { ...sourceMesh.userData }
    ImportedModelObjectMetadataService.copyObjectMetadata(sourceMesh, proxy)

    const instanceMatrix = new THREE.Matrix4()
    if (occurrenceRoot === sourceMesh) {
      const originalParent = controller.resolveOriginalParent(sourceMesh)
      if (!originalParent) {
        proxy.dispose()
        return null
      }
      proxy.position.copy(sourceMesh.position)
      proxy.quaternion.copy(sourceMesh.quaternion)
      proxy.scale.copy(sourceMesh.scale)
      proxy.updateMatrix()
      instanceMatrix.identity()
      originalParent.add(proxy)
    } else {
      const sourceWorldMatrix = new THREE.Matrix4()
      occurrenceRoot.updateWorldMatrix(true, false)
      if (!controller.copyEffectiveWorldMatrix(sourceMesh, sourceWorldMatrix)) {
        proxy.dispose()
        return null
      }
      instanceMatrix
        .copy(occurrenceRoot.matrixWorld)
        .invert()
        .multiply(sourceWorldMatrix)
      occurrenceRoot.add(proxy)
    }

    proxy.setMatrixAt(0, instanceMatrix)
    proxy.instanceMatrix.needsUpdate = true
    return proxy
  }

  /**
   * 收集指定场景根中的活动实例派生根。
   *
   * @param sceneRoot 当前读取根。
   * @returns 保持场景遍历顺序的派生根集合。
   */
  private collectProjectionRoots(sceneRoot: THREE.Object3D): readonly THREE.Object3D[] {
    const projectionRoots: THREE.Object3D[] = []
    sceneRoot.traverse((object) => {
      if (Reflect.get(object, SOURCE_MESH_EXPORT_CONTROLLER)) {
        projectionRoots.push(object)
      }
    })
    return Object.freeze(projectionRoots)
  }

  /**
   * 判断对象是否位于指定根对象的有效父链中。
   *
   * @param object 待判断对象。
   * @param root 目标根对象。
   * @returns 对象是根或其后代时返回 true。
   */
  private isDescendantOrSelf(object: THREE.Object3D, root: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object
    while (current) {
      if (current === root) {
        return true
      }
      current = current.parent
    }
    return false
  }
}

/** Viewer 间共享、按投影根精确隔离的同步导出桥接器。 */
export const importedModelSourceMeshExportBridge = new ImportedModelSourceMeshExportBridge()
